/**
 * TenantContext.js — Phase 21 / Phase 26
 * Routing domaine → tenant + white-label dynamique
 * Charge la config tenant depuis l'API et applique les variables CSS
 */
import React, { createContext, useContext, useState, useEffect, useMemo } from 'react';

const BACKEND = process.env.REACT_APP_BACKEND_URL;

const DEFAULT_TENANT = {
  tenant_id: 'default',
  nom: 'Show Événement',
  domaine: 'show-evenement.com',
  couleur_primaire: '#5DBCBF',
  couleur_secondaire: '#D4AF37',
  logo_url: null,
  slogan: 'Réservez. Composez. Profitez.',
  plan: 'enterprise',
  actif: true,
};

const TenantContext = createContext(DEFAULT_TENANT);

export function TenantProvider({ children }) {
  const [tenant, setTenant] = useState(DEFAULT_TENANT);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    const domain = window.location.hostname;

    // Domaine connu (default) : pas besoin d'appel API
    if (
      domain === 'localhost' ||
      domain === '127.0.0.1' ||
      domain.includes('preview.emergentagent') ||
      domain === 'show-evenement.com'
    ) {
      applyTenantCSS(DEFAULT_TENANT);
      setTenant(DEFAULT_TENANT);
      setLoaded(true);
      return;
    }

    // Domaine inconnu : charger depuis l'API
    fetch(`${BACKEND}/api/public/tenant?domain=${domain}`)
      .then(r => r.ok ? r.json() : DEFAULT_TENANT)
      .then(d => {
        const t = d || DEFAULT_TENANT;
        applyTenantCSS(t);
        setTenant(t);
      })
      .catch(() => applyTenantCSS(DEFAULT_TENANT))
      .finally(() => setLoaded(true));
  }, []);

  // useMemo évite de recréer l'objet à chaque render
  const tenantValue = useMemo(() => ({ tenant, loaded }), [tenant, loaded]);

  return (
    <TenantContext.Provider value={tenantValue}>
      {children}
    </TenantContext.Provider>
  );
}

export function useTenant() {
  return useContext(TenantContext);
}

/**
 * Applique les variables CSS depuis la config tenant
 * Permet un white-label complet (couleurs, logo)
 */
function applyTenantCSS(tenant) {
  const root = document.documentElement;

  if (tenant.couleur_primaire) {
    root.style.setProperty('--tenant-primary', tenant.couleur_primaire);
    // Overrider les variables teal si couleur différente
    if (tenant.couleur_primaire !== '#5DBCBF') {
      root.style.setProperty('--teal', tenant.couleur_primaire);
      root.style.setProperty('--teal-mist', hexToMist(tenant.couleur_primaire));
    }
  }

  if (tenant.couleur_secondaire) {
    root.style.setProperty('--tenant-secondary', tenant.couleur_secondaire);
    if (tenant.couleur_secondaire !== '#D4AF37') {
      root.style.setProperty('--gold', tenant.couleur_secondaire);
    }
  }

  // Titre de la page
  if (tenant.nom && tenant.nom !== 'Show Événement') {
    document.title = tenant.nom;
  }

  // Favicon dynamique si logo fourni
  if (tenant.logo_url) {
    const link = document.querySelector("link[rel*='icon']") || document.createElement('link');
    link.type = 'image/x-icon';
    link.rel = 'shortcut icon';
    link.href = tenant.logo_url;
    document.head.appendChild(link);
  }
}

function hexToMist(hex) {
  // Convertit #RRGGBB en rgba avec opacité faible
  const r = parseInt(hex.slice(1, 3), 16);
  const g = parseInt(hex.slice(3, 5), 16);
  const b = parseInt(hex.slice(5, 7), 16);
  return `rgba(${r},${g},${b},0.08)`;
}

export default TenantContext;
